import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { OrderDetailsSection } from "./ScriptCoContactPreferences";
import ScriptCoRequiredDocuments from "./ScriptCoRequiredDocuments";
import ScriptCoAcknowledgements from "./ScriptCoAcknowledgements";

const ScriptCoPrescriptionOpportunity = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
  customerId,
}) => {
  const productQuantity =
    data.preferences?.productQuantity !== undefined
      ? data.preferences.productQuantity
      : opportunity?.productQuantity ||
        opportunity?.minimumOrderQty ||
        opportunity?.metricQty ||
        "";

  const isProductQtyValid =
    productQuantity !== "" &&
    productQuantity !== null &&
    Number(productQuantity) > 0;

  const [validation, setValidation] = useState({
    documents: false, // Required documents need to be checked
    acknowledgements: false, // Attestation is required
    quantities: isProductQtyValid,
  });

  useEffect(() => {
    setValidation((prev) => {
      if (prev.quantities === isProductQtyValid) return prev;
      return { ...prev, quantities: isProductQtyValid };
    });
  }, [isProductQtyValid]);

  const setDocumentsValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.documents === isValid) return prev;
      return { ...prev, documents: isValid };
    });
  }, []);

  const setAcknowledgementsValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.acknowledgements === isValid) return prev;
      return { ...prev, acknowledgements: isValid };
    });
  }, []);

  useEffect(() => {
    if (onValidationChange) {
      const allValid = Object.values(validation).every((v) => v === true);
      onValidationChange(allValid);
    }
  }, [validation, onValidationChange]);

  const handlePreferencesChange = useCallback(
    (newData) => onDataChange("preferences", newData),
    [onDataChange],
  );

  const hasOrderId = Boolean(
    opportunity?.vendorReferenceId ||
    opportunity?.vendorPreferenceId ||
    opportunity?.orderId ||
    opportunity?.scriptCoOrderDetails?.orderNumber ||
    opportunity?.scriptCoOrderDetails?.id ||
    data?.preferences?.orderId,
  );

  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "stretch" }}>
      {/* Left Column: Order Details + Prescription Documents */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <OrderDetailsSection
          opportunity={opportunity}
          data={{
            patientGuid: customerId,
            ...data.preferences,
          }}
          onDataChange={handlePreferencesChange}
          disabled={hasOrderId}
          showErrors={showErrors}
        />
        <ScriptCoRequiredDocuments
          data={data.uploadedFiles || data.documents}
          onDataChange={useCallback(
            (newData) => onDataChange("uploadedFiles", newData),
            [onDataChange],
          )}
          onValidationChange={setDocumentsValid}
          onFilesChange={useCallback(
            (files) => onDataChange("uploadedFiles", files),
            [onDataChange],
          )}
          customerId={customerId}
          showErrors={showErrors}
          disabled={hasOrderId}
        />
      </Box>

      {/* Right Column Stack: Shipping Details + Acknowledgements */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <ScriptCoAcknowledgements
          data={data.acknowledgements}
          onDataChange={useCallback(
            (newData) => onDataChange("acknowledgements", newData),
            [onDataChange],
          )}
          onValidationChange={setAcknowledgementsValid}
          showErrors={showErrors}
          consentHeader={opportunity?.vendor?.memberConsentHeader}
          consentDetail={opportunity?.vendor?.memberConsentDetail}
          disabled={hasOrderId}
        />
      </Box>
    </Box>
  );
};

ScriptCoPrescriptionOpportunity.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ScriptCoPrescriptionOpportunity;
